import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { ReceiptList } from "@/api/hooks";

function formatTime(unixSec: number) {
  if (!unixSec) return "—";
  return new Date(unixSec * 1000).toISOString().replace("T", " ").slice(0, 19) + "Z";
}

function buildQuery(p: URLSearchParams) {
  const q = new URLSearchParams();
  for (const [k, v] of p.entries()) {
    if (v) q.set(k, v);
  }
  return q.toString();
}

export function ReceiptsPage() {
  const [params, setParams] = useSearchParams();

  // URL is the source of truth for filters; we mirror into local state for
  // the controlled inputs.
  const [deviceId, setDeviceId] = useState(params.get("device_id") ?? "");
  const [verifiedSel, setVerifiedSel] = useState<"" | "true" | "false">(
    (params.get("verified") as "" | "true" | "false") ?? "",
  );
  const [since, setSince] = useState(params.get("since") ?? "");
  const [until, setUntil] = useState(params.get("until") ?? "");
  const [limit, setLimit] = useState(params.get("limit") ?? "50");

  // Debounce free-text filter pushes so we don't refetch every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      setOrDelete(next, "device_id", deviceId);
      setOrDelete(next, "verified", verifiedSel);
      setOrDelete(next, "since", since);
      setOrDelete(next, "until", until);
      setOrDelete(next, "limit", limit === "50" ? "" : limit);
      next.delete("cursor"); // filter change resets pagination
      if (next.toString() !== params.toString()) {
        setParams(next, { replace: true });
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, verifiedSel, since, until, limit]);

  const cursor = params.get("cursor") ?? "";
  const q = useQuery<ReceiptList>({
    queryKey: ["receipts", params.toString()],
    queryFn: () => api<ReceiptList>(`/api/v1/receipts?${buildQuery(params)}`),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-xl font-semibold">Attestation Receipts</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-3xl">
          HMAC-SHA256 receipts emitted by the kit attestation peripheral.
          Reproduce verification offline with{" "}
          <code className="font-mono bg-slate-100 dark:bg-slate-700 px-1 rounded">
            python3 docs/patents/stage1/annexes/A_verify.py
          </code>{" "}
          — no server trust required.
        </p>
      </header>

      <Filters
        deviceId={deviceId}
        setDeviceId={setDeviceId}
        verifiedSel={verifiedSel}
        setVerifiedSel={setVerifiedSel}
        since={since}
        setSince={setSince}
        until={until}
        setUntil={setUntil}
        limit={limit}
        setLimit={setLimit}
      />

      {q.error && (
        <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300 mb-3">
          Could not load receipts: {(q.error as Error).message}
        </div>
      )}

      <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-700 text-left text-xs uppercase tracking-wide text-slate-600 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2">Time (UTC)</th>
              <th className="px-3 py-2">ID</th>
              <th className="px-3 py-2">Decisions</th>
              <th className="px-3 py-2">Session hash</th>
              <th className="px-3 py-2">Size</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Verified</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && !q.data && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500 dark:text-slate-400">Loading…</td></tr>
            )}
            {q.data && q.data.items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500 dark:text-slate-400">No receipts match those filters.</td></tr>
            )}
            {q.data?.items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">
                <td className="px-3 py-2 font-mono whitespace-nowrap text-xs">{formatTime(r.uploaded_at_sec)}</td>
                <td className="px-3 py-2 font-mono">
                  <Link to={`/receipts/${r.id}`} className="text-blue-700 hover:underline">{r.id}</Link>
                </td>
                <td className="px-3 py-2 tabular-nums">{r.decision_count}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.session_hash.slice(0, 16)}…</td>
                <td className="px-3 py-2 tabular-nums">{r.size_bytes} B</td>
                <td className="px-3 py-2 font-mono text-xs">{r.source_ip}</td>
                <td className="px-3 py-2">
                  {r.verified ? (
                    <span className="rounded bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 text-xs font-semibold">YES</span>
                  ) : (
                    <span className="rounded bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-2 py-0.5 text-xs font-semibold">NO</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Link to={`/receipts/${r.id}/verify`} className="text-sm text-blue-700 hover:underline">verify</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <nav className="flex items-center gap-3 mt-4 text-sm">
        <PagerButton
          disabled={!cursor}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.delete("cursor");
            setParams(next);
          }}
          label="← First"
        />
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {q.data?.items.length ?? 0} rows{q.data?.total ? ` of ${q.data.total} total` : ""}
        </span>
        <PagerButton
          className="ml-auto"
          disabled={!q.data?.next_cursor}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.set("cursor", q.data!.next_cursor);
            setParams(next);
          }}
          label="Next →"
        />
      </nav>
    </div>
  );
}

function setOrDelete(p: URLSearchParams, key: string, val: string) {
  if (val) p.set(key, val);
  else p.delete(key);
}

function PagerButton({
  disabled, onClick, label, className,
}: { disabled: boolean; onClick: () => void; label: string; className?: string }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`rounded border border-slate-300 dark:border-slate-600 px-3 py-1 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 ${className ?? ""}`}
    >
      {label}
    </button>
  );
}

interface FiltersProps {
  deviceId: string; setDeviceId: (s: string) => void;
  verifiedSel: "" | "true" | "false"; setVerifiedSel: (s: "" | "true" | "false") => void;
  since: string; setSince: (s: string) => void;
  until: string; setUntil: (s: string) => void;
  limit: string; setLimit: (s: string) => void;
}
function Filters({
  deviceId, setDeviceId, verifiedSel, setVerifiedSel,
  since, setSince, until, setUntil, limit, setLimit,
}: FiltersProps) {
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-3 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3 text-sm">
      <label className="flex flex-col">
        <span className="text-xs text-slate-500 dark:text-slate-400">Device ID</span>
        <input
          type="text"
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value)}
          placeholder="any"
          className="font-mono mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
        />
      </label>
      <label className="flex flex-col">
        <span className="text-xs text-slate-500 dark:text-slate-400">Verified</span>
        <select
          value={verifiedSel}
          onChange={(e) => setVerifiedSel(e.target.value as "" | "true" | "false")}
          className="mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-800"
        >
          <option value="">any</option>
          <option value="true">YES only</option>
          <option value="false">NO only</option>
        </select>
      </label>
      <label className="flex flex-col">
        <span className="text-xs text-slate-500 dark:text-slate-400">Since (unix sec)</span>
        <input
          type="text"
          value={since}
          onChange={(e) => setSince(e.target.value)}
          placeholder="0"
          className="font-mono mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
        />
      </label>
      <label className="flex flex-col">
        <span className="text-xs text-slate-500 dark:text-slate-400">Until (unix sec)</span>
        <input
          type="text"
          value={until}
          onChange={(e) => setUntil(e.target.value)}
          placeholder="0"
          className="font-mono mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
        />
      </label>
      <label className="flex flex-col">
        <span className="text-xs text-slate-500 dark:text-slate-400">Limit</span>
        <select
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          className="mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-800"
        >
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
          <option value="200">200</option>
          <option value="500">500</option>
        </select>
      </label>
    </div>
  );
}
